/* The template contract.
   A template is a size, a list of fields to fill in, and a function that
   paints them onto a canvas. Adding one means adding a file and a line
   in templates/index.ts — nothing in the editor or the API changes. */

/** How much of the editor's two-column form a field takes. */
type Width = { width?: 'half' | 'full' };

export type Field =
  /** One line of text. */
  | ({ key: string; label: string; kind: 'text'; hint?: string; max?: number } & Width)
  /** Several lines; the template decides how many it will draw. */
  | ({ key: string; label: string; kind: 'lines'; hint?: string; rows?: number } & Width)
  /** '1' or '' — stored as a string so every field value is one type. */
  | ({ key: string; label: string; kind: 'toggle' } & Width)
  /** A number held as a string, shown as a slider. */
  | ({
      key: string;
      label: string;
      kind: 'range';
      min: number;
      max: number;
      step?: number;
      suffix?: string;
    } & Width);

export type FieldGroup = { title: string; fields: Field[] };

export type Values = Record<string, string>;

export type Template = {
  id: string;
  name: string;
  /** One line describing what this template is for, shown in the picker. */
  blurb: string;
  width: number;
  height: number;
  /** True when the template paints a supplied picture, so the editor
      knows to ask for one and to warn when none has been chosen. */
  usesPicture: boolean;
  groups: FieldGroup[];
  defaults: Values;
  /** Font specifiers this template needs loaded before it will draw
      correctly. Canvas silently falls back to a system face otherwise,
      which would quietly ruin a downloaded poster. */
  fonts: string[];
  draw: (ctx: CanvasRenderingContext2D, values: Values, picture: HTMLImageElement | null) => void;
};

/** Every field in a template, flattened — handy for defaults and diffing. */
export function allFields(t: Template): Field[] {
  return t.groups.flatMap((g) => g.fields);
}

/** Fill in anything the stored values are missing, so an older ad still
    renders after a template gains a field. */
export function withDefaults(t: Template, values: Values): Values {
  return { ...t.defaults, ...values };
}

export const num = (v: string | undefined, fallback: number): number => {
  const n = Number(v);
  return Number.isFinite(n) ? n : fallback;
};
